/**
 * 관리자 유틸리티
 * - 팝업 윈도우 관련 함수 통합
 */
import CommonUtil from '../../Common/Util';

export default class Util {

    /**
     * 경량 자동완성 (키보드 ↓/↑/Enter/Esc 지원) — input 바로 아래 드롭다운.
     * input 을 position:relative 래퍼로 감싸 부모 패딩과 무관하게 입력칸에 정확히 정렬한다.
     *
     * @param {string|HTMLElement|jQuery} input  대상 input
     * @param {object} opts
     *   - url       {string}            GET 자동완성 URL (kwd 파라미터로 검색어 전송)
     *   - render    {(row)=>string}     항목 1개의 innerHTML
     *   - pick      {(row)=>void}       선택(마우스/Enter) 시 콜백
     *   - minLen    {number=2}          최소 입력 글자수
     *   - debounce  {number=200}        입력 디바운스(ms)
     *   - params    {()=>object}        추가 쿼리 파라미터(선택)
     */
    static autocomplete(input, opts = {}) {
        const $in = $(input);
        if (!$in.length || $in.data('ac-bound')) return;
        // 브라우저 자동완성 끄기 — `autocomplete="off"` 만으로는 **크롬이 무시한다.**
        // 이름·전화 같은 칸은 크롬이 저장값 드롭다운을 자체 판단으로 띄우는데,
        // 우리 자동완성 목록과 겹쳐 떠서 고르기가 불편했다.
        // 표준에 없는 토큰(`new-password`)을 주면 크롬이 판단을 포기한다 — 이게 유일하게 통한다.
        // data-lpignore / data-form-type 은 LastPass · 1Password 용.
        $in.data('ac-bound', true)
           .attr({ autocomplete: 'new-password', 'data-lpignore': 'true', 'data-form-type': 'other' });

        const { url, render, pick } = opts;
        const minLen   = opts.minLen ?? 2;
        const debounce = opts.debounce ?? 200;
        const extra    = opts.params || (() => ({}));

        // input 을 블록 래퍼로 감싸 드롭다운 정렬 기준을 입력칸으로 고정 (전체폭 form-control 폭 보존)
        $in.wrap('<div class="ac-wrap" style="position:relative"></div>');
        const $ul = $('<ul></ul>').css({
            display: 'none', position: 'absolute', top: '100%', left: 0,
            zIndex: 2000, minWidth: '100%', maxHeight: '260px', overflow: 'auto',
            margin: '2px 0 0', padding: 0, listStyle: 'none', background: 'var(--adm-b-ffffff, #fff)', whiteSpace: 'nowrap',
            border: '1px solid #ccc', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,.12)',
        }).appendTo($in.parent());

        let timer = null, last = '', rows = [], idx = -1;

        const close = () => { $ul.hide().empty(); rows = []; idx = -1; };
        const paint = () => $ul.children('li').each(function (i) {
            $(this).css('background', i === idx ? '#e7f1ff' : '');
            if (i === idx) this.scrollIntoView({ block: 'nearest' });
        });
        const choose = (i) => {
            if (i < 0 || i >= rows.length) return;
            const r = rows[i];
            close();
            last = $in.val();
            pick && pick(r);
        };
        const fetch = () => {
            const kw = $in.val().trim();
            // ⚠️ 짧아지면 `last` 도 비운다. 안 그러면 '이재일' → 지움 → 다시 '이재일' 일 때
            //    kw === last 로 걸려 목록이 영영 안 뜬다 (지운 사이 요청이 없었는데도).
            if (kw.length < minLen) { last = ''; close(); return; }
            if (kw === last) return;
            last = kw;
            $.getJSON(url, Object.assign({ kwd: kw }, extra()), data => {
                rows = data || []; idx = -1;
                if (!rows.length) { close(); return; }
                $ul.empty();
                rows.forEach((r, i) => {
                    $('<li>').html(render(r))
                        .css({ padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '12px' })
                        .on('mouseenter', () => { idx = i; paint(); })
                        .on('mousedown', e => { e.preventDefault(); choose(i); }) // blur 보다 먼저
                        .appendTo($ul);
                });
                $ul.show();
            });
        };

        $in.on('input', () => { clearTimeout(timer); timer = setTimeout(fetch, debounce); });
        $in.on('keydown', (e) => {
            if (!$ul.is(':visible') || !rows.length) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); idx = (idx + 1) % rows.length; paint(); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); idx = (idx - 1 + rows.length) % rows.length; paint(); }
            else if (e.key === 'Enter' && idx >= 0) { e.preventDefault(); e.stopPropagation(); choose(idx); }
            else if (e.key === 'Escape') { e.stopPropagation(); close(); }
        });
        $in.on('blur', () => setTimeout(close, 150));
    }

    /**
     * 글자크기 배율 — 관리자가 고른 확대 배율 (보통 1 · 크게 1.15 · 아주크게 1.30).
     *
     * 정본은 CSS 변수 `--adm-zoom` 이다 (admin/base/_fontsize.scss 가 `<html data-fs>` 로 건다).
     * 저장값(localStorage)이 아니라 **지금 이 문서에 실제로 걸린 값**을 읽는다 — 잠긴 화면처럼
     * 저장값과 적용값이 다를 수 있기 때문이다.
     */
    static zoomScale() {
        const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--adm-zoom'));
        return v > 0 ? v : 1;
    }

    /**
     * 팝업 윈도우 (화면 중앙 정렬)
     *
     * ⚠ **크기를 글자크기 배율만큼 키운다.** 팝업 안의 내용은 `body{zoom}` 으로 확대되는데
     *   창 크기는 px 고정이라, 확대하면 내용이 창에 안 들어가 잘렸다 (/adm/member/edit 에서
     *   보고됨). 화면 밖으로 나가지 않게 `screen.avail*` 로 자른다.
     *
     *   기본은 **내용이 커지면 창도 키운다.** `scale:false` 로 빼는 경우는 둘.
     *   ① 관리자가 아닌 사이트 — 회원 모바일화면(/m/)·악덕(/b/)은 그쪽 회원의 글자크기를 따른다.
     *   ② 창은 그대로 두고 **안에서 흡수**하기로 한 화면 — 기사편집이 그렇다. 열1~3 이 고정폭이라
     *      확대분을 상담열(1fr)이 줄어들며 받는다. 창까지 키우면 화면을 다 덮는다.
     *
     * @param {string} url
     * @param {object} options - { name, width, height, resizable, scale }
     */
    static popup(url, options = {}) {
        const o = { name: '_blank', width: 500, height: 800, resizable: true, scale: true, ...options };
        if (o.scale) {
            const z = Util.zoomScale();
            o.width  = Math.min(Math.round(o.width  * z), window.screen.availWidth);
            o.height = Math.min(Math.round(o.height * z), window.screen.availHeight);
        }
        // left/top 을 주면 그 자리에, 없으면 화면 중앙. (기사관리 '옆에 붙여 열기' 가 left/top 을 준다)
        const x = (o.left !== undefined) ? o.left : (window.screen.width / 2) - (o.width / 2);
        const y = (o.top  !== undefined) ? o.top  : (window.screen.height / 2) - (o.height / 2);
        const features = `toolbar=no,status=no,directories=no,scrollbars=yes,location=no,menubar=no,resizable=${o.resizable ? 'yes' : 'no'},WIDTH=${o.width},HEIGHT=${o.height},left=${x},top=${y}`;
        const win = window.open(url, o.name, features);
        if (win) win.focus();
        return win;
    }

    /**
     * 기사관리 창 — **관리자에서 회원을 여는 유일한 입구.**
     *
     * 예전에는 `/adm/member/edit` 를 직접 여는 입구가 따로 있었는데(이 메서드·admin.js
     * member_popup_win), 그러면 "우측 모바일화면을 접어둔다"는 취향이 어느 목록에서 열었느냐에 따라
     * 지켜지기도 하고 안 지켜지기도 했다. 지금은 전부 포털로 보낸다 — **접힌 포털이 곧 편집창만
     * 있는 화면**이라 굳이 두 주소를 유지할 이유가 없다 (2026-08 요청).
     *
     * 예외는 신규등록(mbNo=0) 하나다. 포털의 우측은 `/m/login/proxy?work_num=` 대리로그인이라
     * 아직 없는 회원으로는 열 수 없다 → 편집창 단독으로 띄운다.
     */
    static memberEdit(mbNo, winName = 'member-edit-popup') {
        if (CommonUtil.int(mbNo) > 0) return this.memberPortal(mbNo);
        // 창은 그대로 두고 상담열(1fr)이 줄며 확대분을 흡수한다 → 배율을 곱하지 않는다
        return this.popup('/adm/member/edit?mb_no=' + mbNo, { name: winName, width: 1300, height: 820, scale: false });
    }

    /**
     * 기사관리 팝업 (모바일이면 좁게 — 좁은 화면에 두 창은 못 넣는다)
     *
     * 주소는 편집창 하나뿐이다 — 우측 모바일화면은 그 안에 들어 있다(2026-08, `.me-shell`).
     * 예전엔 `/adm/member/portal` 셸을 거쳤다.
     *
     * 우측창을 접어둔 직원에게는 **팝업 폭도 같이 줄인다**(portalWidth) — 1700px 창에
     * 1250px 편집 본문만 남으면 오른쪽이 휑하다.
     */
    static memberPortal(mbNo, url = '', beside = false) {
        if ($j.Common.Util.isMobile()) {
            return this.popup('/adm/member/edit?mb_no=' + mbNo,
                { name: 'member-edit-popup', width: 1300, height: 820, scale: false });
        }
        let adr = `/adm/member/edit?mb_no=${mbNo}`;
        if (url) adr += `&url=${encodeURIComponent(url)}`;  // 우측 앱 화면 진입 경로(예: /m/chat)

        // 창은 그대로 두고 상담열(1fr)이 줄며 확대분을 흡수한다 → 배율을 곱하지 않는다
        const opts = { name: 'memberadd', width: this.portalWidth(), height: 820, scale: false };
        if (beside) {
            // 현재 창(예: /adm/chat/room) 바로 오른쪽에 붙여서 띄운다 (tarLinkTo 와 동일)
            const baseLeft = (window.screenLeft !== undefined) ? window.screenLeft : window.screenX;
            const baseTop  = (window.screenTop  !== undefined) ? window.screenTop  : window.screenY;
            opts.left = baseLeft + window.outerWidth;
            opts.top  = baseTop;
        }
        return this.popup(adr, opts);
    }

    /**
     * **편집창 안에서 다른 회원을 여는 입구** (기사 / 장비 관리 모달의 기사 바로가기).
     *
     * 위 `memberPortal` 을 그대로 쓰면 안 되는 이유가 둘이다.
     *
     * 1) **창 이름.** 편집창은 보통 `memberadd` 라는 이름으로 열려 있다. 그 안에서 같은 이름으로
     *    `window.open` 하면 브라우저가 **지금 이 창을 재사용**해서 새 창이 아니라 화면 이동이 된다.
     *    그래서 회원별 이름을 쓴다 — 같은 사람을 두 번 누르면 그 창이 앞으로 나온다.
     * 2) **우측 모바일화면은 접어서 연다** (`pane=0`). 기사를 잠깐 확인하러 여는 창이라
     *    대리로그인까지 붙일 이유가 없다. 취향 키(`G5.LS_ADM_PORTAL_PANE`)는 건드리지 않는다 —
     *    목록에서 정상적으로 열 때는 그대로 펼쳐져야 한다.
     */
    static memberEditSub(mbNo) {
        const url = `/adm/member/edit?mb_no=${mbNo}&pane=0`;
        if ($j.Common.Util.isMobile()) {
            return this.popup(url, { name: 'member-edit-popup', width: 1300, height: 820, scale: false });
        }
        return this.popup(url, { name: `member-edit-${CommonUtil.int(mbNo)}`, width: 1300, height: 820, scale: false });
    }

    /**
     * 기사관리 팝업 폭 — 우측창 취향에 따라 1700(펼침) / 1300(접힘).
     *
     * 취향 키의 정본은 common.js 의 `G5.LS_ADM_PORTAL_PANE` 이고, 편집창
     * (edit-v2.blade.php 선행 스크립트 · MemberEdit._initPortalPane)이 같은 키를 읽고 쓴다.
     * **폭 값도 그쪽과 같아야 한다** — 편집창은 토글할 때 window.resizeTo 로 같은 두 값
     * 사이를 오간다. 한쪽만 고치면 처음 열 때와 접었다 편 뒤의 폭이 달라진다.
     *
     * 여기서도 배율을 곱하지 않는다 — 기사편집은 글자크기 축이 잠겨 내용이 확대되지 않는다
     * (edit-v2.blade.php `@section('themeLockAxes','theme,fontsize')`).
     */
    static portalWidth() {
        return $j.Common.Store.local.get(G5.LS_ADM_PORTAL_PANE) === 'closed' ? 1300 : 1700;
    }

    /** 회원 모바일화면 팝업 (500x800) */
    static userMobile(url) {
        // 회원 모바일 사이트(/m/) — 관리자 글자크기와 무관하다
        return this.popup(url, { name: 'memberinfowinsss', width: 500, height: 800, scale: false });
    }
    /** 회원 발주페이지 주소 */
    static getUserBal(mb_no,ordernum=0){
        let return_url = '/m/bal';
        if(ordernum) return_url = '/m/bal';// 오더수정일때는 신규발주 페이지로

        if(ordernum){
            return_url+=`?mode=myorderedit&editnum=${ordernum}`;
        }
        return "/m/login/proxy?work_num=" + mb_no+'&returnurl='+encodeURIComponent(return_url);
    }

    /** 회원 발주화면 팝업 */
    static userBal(mbNo, ordernum = 0) {
        return this.userMobile(this.getUserBal(mbNo, ordernum));
    }

    /** 채팅 팝업 */
    static chat(rno) {
        return this.popup("/adm/chat?rno=" + rno, { name: String(rno), width: 400, height: 600 });
    }

    /** 악덕알리미 회원 모바일 팝업 (대리로그인 프록시) */
    static devilMobile(mbNo, returnUrl = '/b') {
        if (!mbNo) return;
        return this.popup('/b/login/proxy?work_num=' + mbNo + '&returnurl=' + encodeURIComponent(returnUrl),
            // 악덕 사이트(/b/) — 관리자 글자크기와 무관하다
            { name: 'dvMemberMobile_' + mbNo, width: 430, height: 880, scale: false });
    }

    /** 채팅 목록 */
    static chatList() {
        return this.popup("/adm/chat/room", { width: 450, height: 810 });
    }
    /**
     * 새로운 채팅이 올라왔는지 체크 하여 관리자에 표시하고 새창을 띄운다.
     */
    static Talk_notice_check(){
        const self = this;
        $.ajax({type:"POST", url:"/adm/ajax/chat/new", dataType:'json', success:function(data){
            if( data['room_num'] ){
                self.popup("/adm/chat?rno="+data['room_num'], {name: 'adminChatPop', width: 450, height: 600, resizable: false});
            }
        }});
    }

    /** 회원 오더화면 팝업 */
    static userOrder(num, ordernum = 0, t = '') {
        if (!num) return;
        let url = "/m/login/proxy?work_num=" + num;
        let reurl = '';
        if (ordernum) reurl = '/m/diary?showipicknum=' + ordernum;
        if (t === '추가배차') reurl = "/m/bal?mode=otherorder&editnum=" + ordernum;
        url += "&returnurl=" + encodeURIComponent(reurl);
        return this.userMobile(url);
    }

    /** 회원 서류보기 팝업 (회원 모바일 대리로그인 → 서류 화면) */
    static userDocs(mbNo) {
        const url = "/m/login/proxy?work_num=" + mbNo + '&returnurl=' + encodeURIComponent('/m/docs/v1');
        return this.userMobile(url);
    }

    /** 관리자 서류심사 팝업 (/adm/member/doc — 대리로그인 아님, 관리자 화면) */
    static memberDocEdit(mbNo) {
        return this.popup(`/adm/member/doc?mb_no=${mbNo}`, { name: 'member_doc', width: 1024, height: 800, scale: false });
    }

    /** 회원 캐시내역 팝업 */
    static userCash(mbNo, workDay = '') {
        if (!mbNo) return;
        let returnUrl = '/m/cash/list';
        if (workDay) returnUrl += '&work_day=' + workDay;
        const url = "/m/login/proxy?work_num=" + mbNo + '&returnurl=' + encodeURIComponent(returnUrl);
        return this.userMobile(url);
    }

    /** 회원 포인트내역 팝업 */
    static userPoint(mbNo) {
        if (!mbNo) return;
        const returnUrl = '/m/point';
        const url = "/m/login/proxy?work_num=" + mbNo + '&returnurl=' + encodeURIComponent(returnUrl);
        return this.userMobile(url);
    }

    /**
     * 토스트 메세지 표시
     * @param icon
     * @param title
     */
    static tsalert(icon,title,position='top-right'){
        // position 인자는 하위호환용(무시) — 공용 토스트는 사이트별 위치/테마 자동 적용
        $j.Ui.Toast.show(title, { type: icon, timeout: 3000 });
    }

    /**
     * 관리자 토스트 — tsalert 와 시그니처가 달라(타입이 먼저·timeout 지정) 별도로 둔다.
     * 공용 $j.Ui.Toast 가 아직 안 선 아주 이른 시점엔 알림을 삼키지 않도록 swalert 로 떨어진다.
     *
     * @param {string} type    success | error | warning | info (그 밖은 success)
     * @param {string} message
     * @param {number} [timeout=2500]
     */
    static adminToast(type, message, timeout) {
        type = (type === 'error' || type === 'warning' || type === 'info') ? type : 'success';
        if (window.$j && $j.Ui && $j.Ui.Toast) {
            $j.Ui.Toast.show(message, { type, timeout: timeout || 2500 });
            return;
        }
        CommonUtil.swalert(message);
    }

    /**
     * 오더 핸들 팝업(/adm/order/handle) — 관리자 공용.
     * @param {number} ordernum 오더번호
     * @param {Object} [opts]
     * @param {string} [opts.right] 우측 영역 기본값('settle'=정산, 미지정=발주 미리보기)
     * @param {string} [opts.name]  창 이름(기본 'manage_pop_show_order_detail' — 단일 창 재사용)
     * @returns {Window|null}
     */
    static openOrderHandle(ordernum, opts = {}) {
        const q = opts.right ? '&right=' + opts.right : '';
        const name = opts.name || 'manage_pop_show_order_detail';
        return this.popup('/adm/order/handle?ordernum=' + ordernum + q,
            { name, width: 1000, height: 1060, scale: false });
    }

    /**
     * 로그인 기록 모달 (공용) — g5_login_log 조회 후 DataTable 로 표시.
     * 환경설정(앱마켓 심사용 계정)·기사관리(회원) 등에서 공통 사용.
     * 응답 형식 {result, mb_no, mem_id, mem_name, list:[{datetime,res,ip,what,why,where}]} 기대.
     * what(무엇·어떻게)·why(사유)·where(앱/브라우저·기종·기기) 문구는 서버가 만든다(LoginLogService::forMember) —
     * 화면마다 라벨표를 두면 어긋난다. 보관은 1년(BatchController).
     *
     * 누구의 기록인지는 **제목**이 말한다(`제목 — 이름#번호`, 관리자 회원 표기 규약). 본문은 표뿐이다.
     * 서버가 이미 최신순으로 주므로 정렬은 끄고, 쪽은 클라이언트에서 나눈다(최대 100건).
     * DataTables 는 관리자 레이아웃(main·bare) 둘 다 싣는다 — 기사편집 팝업(bare)에서도 뜬다.
     *
     * @param {Object} opts
     * @param {string} opts.url       조회 URL (필수)
     * @param {string} [opts.method]  HTTP 메서드 (기본 GET)
     * @param {string} [opts.title]   모달 제목 (기본 '로그인 기록')
     * @param {string} [opts.modalId] 모달 DOM id (기본 'login-logs-modal')
     */
    static showLoginLogs(opts = {}) {
        if (!opts.url) { this.tsalert('error', '조회 URL 이 없습니다.'); return; }
        const self = this;
        const modalId = opts.modalId || 'login-logs-modal';
        const tblId   = `${modalId}-tbl`;
        $.ajax({
            type: opts.method || 'GET',
            dataType: 'json',
            url: opts.url,
            data: opts.data || undefined,   // 조회 대상을 넘겨야 하는 화면용 (예: mb_no)
            success(data) {
                if (!data.result) {
                    self.tsalert('error', data.message || '조회에 실패했습니다.');
                    return;
                }
                const esc = (v) => $('<i>').text(v == null ? '' : v).html();
                const badge = (txt, bg) => `<span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:12px;color:#fff;background:${bg}">${txt}</span>`;

                const rows = (data.list || []).map((r) => {
                    const resBadge = r.res === 'success' ? badge('성공', '#28a745') : badge('실패', '#dc3545');
                    const why = r.why ? `<div style="font-size:12px;opacity:.75">${esc(r.why)}</div>` : '';
                    return `<tr><td>${esc(r.datetime)}</td><td>${esc(r.what)}</td>`
                         + `<td class="text-center">${resBadge}${why}</td><td>${esc(r.where)}</td><td>${esc(r.ip)}</td></tr>`;
                }).join('');

                const html = `<div style="padding:8px 4px">
                    <table id="${tblId}" class="table table-bordered display" style="width:100%;font-size:13px">
                        <thead><tr><th style="width:140px">일시</th><th>내용</th><th style="width:96px">결과</th><th>기기</th><th>IP</th></tr></thead>
                        <tbody>${rows}</tbody>
                    </table>
                </div>`;

                // 같은 모달을 다시 열면 이전 표의 DataTable 인스턴스부터 걷어낸다
                if ($.fn.DataTable.isDataTable(`#${tblId}`)) $(`#${tblId}`).DataTable().destroy();
                $j.Ui.JBModal.html(modalId, html, '', { width: '860px' });

                const who = `${data.mem_name || '회원'}#${data.mb_no}`;
                $(`#${modalId}`).find('.ui-modal__title').text(`${opts.title || '로그인 기록'} — ${who}`);

                new DataTable(`#${tblId}`, {
                    autoWidth: false, searching: false, lengthChange: false, info: false, ordering: false, pageLength: 15,
                    language: { emptyTable: '로그인 기록이 없습니다.', paginate: { previous: "<i class='fa fa-chevron-left'></i>", next: "<i class='fa fa-chevron-right'></i>" } },
                });
            },
            error() {
                self.tsalert('error', '조회 중 오류가 발생했습니다.');
            }
        });
    }

    /**
     * 모달 공용 페이저 — 서버가 준 `{page, pages, total}` 로 그린다. 한 쪽뿐이면 그리지 않는다.
     * 쪽번호는 현재쪽 앞뒤 2개까지만 — 모달이 560~820px 이라 전부 늘어놓으면 줄바꿈이 난다.
     * 버튼은 `.me-pg__btn[data-page]` 라 쓰는 쪽이 모달 본문에 위임으로 클릭을 받는다.
     * 모양은 `.me-pg` (sass/admin/pages/_member-edit-v2.scss — adm.css 전역).
     */
    static pager(d) {
        const pages = CommonUtil.int(d.pages) || 1;
        if (pages <= 1) return '';

        const cur  = Math.min(Math.max(1, CommonUtil.int(d.page) || 1), pages);
        const from = Math.max(1, cur - 2);
        const to   = Math.min(pages, cur + 2);
        const btn  = (p, label, cls = '') =>
            `<button type="button" class="me-pg__btn ${cls}" data-page="${p}">${label}</button>`;

        let out = '';
        if (cur > 1) out += btn(1, '&laquo;') + btn(cur - 1, '&lsaquo;');
        for (let i = from; i <= to; i++) out += btn(i, i, i === cur ? 'is-on' : '');
        if (cur < pages) out += btn(cur + 1, '&rsaquo;') + btn(pages, '&raquo;');

        return `<div class="me-pg">${out}
            <span class="me-pg__sum">${cur} / ${pages} 쪽 · 전체 ${Number(d.total ?? 0).toLocaleString()}건</span>
        </div>`;
    }

    /**
     * 제재 딱지 — `g5_member_owners.penalty` 한 값만 보고 그린다.
     *
     * 등급은 PenaltyService::CARD_* 와 같다.
     *   0 정상 / 1 경고(노랑) / 2 차단(분홍 — 발주·수주·출금금지, 수발주제한(상대)) / 3 환원페널티(빨강)
     *
     * 예전엔 목록마다 이 마크업을 복사해 뒀고, 환원인지 알려고 화면에 따라
     * `g5_order_penalty` 서브쿼리를 하나 더 달기도 했다. 캐시 컬럼이 환원까지
     * 흡수한 뒤로는(2026-08) **조인 없이 이 함수 하나**로 끝난다.
     *
     * 색·테두리는 SCSS 의 `.me-pcard` 에 있다 (경고 = 노랑, 그외 제재 = 분홍, 환원페널티 = 빨강, 검은 실선).
     *
     * @param {number|string} penalty g5_member_owners.penalty (Sql 은 문자열로 준다)
     * @param {string} [tail=''] 딱지 뒤에 붙일 문자열 (딱지가 없으면 붙지 않는다)
     * @returns {string} 딱지 HTML ('' = 정상)
     */
    static penaltyCard(penalty, tail = '') {
        const v = parseInt(penalty, 10) || 0;
        if (v <= 0) return '';

        const [title, cls] = v >= 3 ? ['환원페널티', 'whan']
                           : v >= 2 ? ['제재회원',   'ban']
                           :          ['경고회원',   'warn'];

        return `<span title="${title}" class="me-pcard me-pcard--${cls}">&nbsp;&nbsp;&nbsp;</span>${tail}`;
    }

    /**
     * 관리자 회원 검색칸 — 자동완성 + '이름#번호' 규약.
     *
     * `$j.F.Admin.AutoComplete` 의 기본 onSelect 는 인풋에 **이름만** 넣고 회원번호는
     * `data-value` 에 숨긴다. 그러면 표시값과 실제 검색기준이 어긋나도 사용자가 알 수 없다
     * (CLAUDE.md '검색 인풋에 숨은 상태를 두지 않는다' · 접수목록에서 이미 겪은 문제).
     * 그래서 고른 순간 **보이는 텍스트 자체를 검색기준**으로 만든다.
     *
     *   이재일#2831 → 회원번호 2831 한 명 (자동완성에서 고름)
     *   이재일       → 이름검색 (같은 이름 전원)
     *
     * 서버는 `MemberQueryService::resolveSearchNums()` 가 같은 규약으로 되받는다.
     * 두 곳의 규칙이 어긋나면 화면에 보이는 값과 결과가 달라지므로 짝으로 고친다.
     *
     * @param {string} id 검색 input 의 id
     * @param {Function} [onPick] 자동완성에서 고른 뒤 부른다 — 고르자마자 다시 조회하는 화면용 (안 넘기면 동작 동일)
     */
    static memberSearch(id, onPick) {
        $j.F.Admin.AutoComplete.init({
            id,
            onSelect: (inputId, $li) => {
                $(`#${inputId}`).val(`${$li.data('name')}#${$li.data('num')}`).data('value', '');
                if (onPick) onPick(inputId);
            },
        });
    }

}
